import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
Package,
Plus,
Search,
Edit,
Trash2,
AlertTriangle,
Eye,
RefreshCw
} from 'lucide-react';
import { productsAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import PageToolbar from '../components/PageToolbar';
import { useNavigate } from 'react-router-dom';

// Not: Kategori kodları artık backend'de yönetiliyor



const Products = () => {
    const {
        user,
        userRole,
        isWarehouse,
        canCreateProduct,
        canUpdateProduct,
        canDeleteProduct,
        canEditProductPrices
    } = useAuth();
    const { showSuccess, showError } = useNotification();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        brands: []
    });
    const [showLowStock, setShowLowStock] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');



    // Form state for textile products
    const [formData, setFormData] = useState({
        name: '',
        product_code: '',
        brand: '',
        ...(canEditProductPrices() ? { cost: '', price: '' } : {}),
        stock: ''
    });
    const [stockToAdd, setStockToAdd] = useState('');

    // Reset form - tanımlamayı erken yapalım
    const resetForm = useCallback(() => {
        setFormData({
            name: '',
            product_code: '',
            brand: '',
            ...(canEditProductPrices() ? { cost: '', price: '' } : {}),
            stock: ''
        });
        setStockToAdd('');
        setShowAddModal(false);
        setShowEditModal(false);
        setSelectedProduct(null);
    }, [canEditProductPrices]);

    // Debounced search
    const searchTimeoutRef = useRef(null);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    // Debounce search term
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm]);

    // Fetch products
    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                page: currentPage,
                limit: 50,
                search: debouncedSearchTerm,
                lowStock: showLowStock,
                sortField: sortField,
                sortDirection: sortDirection
            };

            const response = await productsAPI.getProducts(params);
            console.log('Products response:', response.data.products);
            setProducts(response.data.products);
            setTotalPages(response.data.pagination.totalPages);
        } catch (error) {
            console.error('Fetch products error:', error);
            console.error('Error response:', error.response?.data);
            console.error('Error status:', error.response?.status);
            console.error(error.response?.data?.message || 'Ürünler yüklenemedi. Lütfen tekrar deneyin.', { duration: 4000 });
        } finally {
            setLoading(false);
        }
    }, [currentPage, debouncedSearchTerm, showLowStock, sortField, sortDirection]);





    // Fetch filter options
    const fetchFilters = async () => {
        try {
            const response = await productsAPI.getFilterOptions();
            setFilters(response.data);
        } catch (error) {
            console.error('Fetch filters error:', error);
            // Hata durumunda varsayılan değerler
            setFilters({
                brands: []
            });
        }
    };

    // Handle escape key to close modals
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                if (showAddModal || showEditModal) {
                    resetForm();
                }
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [showAddModal, showEditModal, resetForm]);


    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    useEffect(() => {
        fetchFilters();
    }, []);

    // Handle stock addition
    const handleAddStock = async () => {
        const addAmount = parseFloat(stockToAdd) || 0;
        if (addAmount > 0) {
            try {
                const currentStock = parseFloat(formData.stock) || 0;
                const newStock = currentStock + addAmount;

                console.log('Adding stock in Products.js with amount:', addAmount, 'User role:', userRole);

                // Update stock and create movement record
                await productsAPI.updateStock(selectedProduct.id, addAmount, {
                    movementType: 'in',
                    referenceType: 'manual_adjustment',
                    notes: `Manuel Stok Ekleme - ${addAmount} adet`,
                    createdBy: user?.id
                });

                console.log('Stock update completed in Products.js');

                setFormData(prev => ({ ...prev, stock: newStock.toString() }));
                setStockToAdd('');
                showSuccess(`${addAmount} adet stok eklendi. Yeni stok: ${newStock}`);
            } catch (error) {
                console.error('Add stock error:', error);
                showError('Stok eklenirken hata oluştu');
            }
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (showEditModal && selectedProduct) {
                await productsAPI.updateProduct(selectedProduct.id, formData, user?.id, userRole);
                showSuccess('Ürün bilgileri başarıyla güncellendi!');
            } else {
                await productsAPI.createProduct(formData, user?.id, userRole);
            }

            resetForm();
            fetchProducts();
            fetchFilters(); // Refresh filters in case new options were added
        } catch (error) {
            console.error('Product operation error:', error);
            console.error('Error response:', error.response?.data);

            let message = 'İşlem başarısız';
            if (error.response?.data?.message) {
                message = error.response.data.message;
            } else if (error.response?.data?.error) {
                message = `${error.response.data.error}: ${error.response.data.message || ''}`;
            } else if (error.message) {
                message = error.message;
            }

            showError(`Hata: ${message}`);
        }
    };

    // Handle delete
    const handleDelete = async (productId) => {
        if (window.confirm('Bu ürünü tamamen silmek istediğinizden emin misiniz? Bu işlem geri alınamaz ve ürünle ilgili tüm veriler silinecektir.')) {
            try {
                await productsAPI.deleteProduct(productId);
                showSuccess('Ürün tamamen silindi');
                fetchProducts();
            } catch (error) {
                console.error('Delete product error:', error);
                console.error('Error details:', error.response?.data);

                let errorMessage = 'Ürün silinemedi. Lütfen tekrar deneyin.';

                if (error.message?.includes('foreign key constraint')) {
                    errorMessage = 'Bu ürün satış kayıtlarında kullanıldığı için silinemez. Önce ilgili satış kayıtlarını silin.';
                } else if (error.message?.includes('violates foreign key constraint')) {
                    errorMessage = 'Bu ürün başka kayıtlarda kullanıldığı için silinemez. İlgili kayıtları kontrol edin.';
                } else if (error.response?.data?.message) {
                    errorMessage = `Hata: ${error.response.data.message}`;
                } else if (error.message) {
                    errorMessage = `Hata: ${error.message}`;
                }

                showError(errorMessage);
            }
        }
    };

    // Handle edit
    const handleEdit = (product) => {
        try {
            setSelectedProduct(product);
            setFormData({
                name: product.name || '',
                product_code: product.product_code || '',
                brand: product.brand || '',
                ...(canEditProductPrices() ? {
                    cost: product.cost || '',
                    price: product.price || ''
                } : {}),
                stock: product.stock || ''
            });

            setShowEditModal(true);
        } catch (error) {
            console.error('Ürün düzenleme hatası:', error);
            showError('Ürün düzenleme sırasında bir hata oluştu. Lütfen tekrar deneyin.');
        }
    };

    // Handle sorting
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Handle input change
    const handleInputChange = (e) => {
        try {
            const { name, value } = e.target;
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));

        } catch (error) {
            console.error('Input değişiklik hatası:', error);
        }
    };







    // Calculate stats
    const calculateStats = () => {
        const totalProducts = products.length;
        const lowStockProducts = products.filter(p => p.stock <= 0).length;

        // Alış fiyatı ile toplam değer
        const totalValueByCost = isWarehouse() ? 0 : products.reduce((sum, p) => sum + (p.stock * (parseFloat(p.cost) || 0)), 0);

        // Satış fiyatı ile toplam değer
        const totalValueByPrice = isWarehouse() ? 0 : products.reduce((sum, p) => sum + (p.stock * (parseFloat(p.price) || 0)), 0);

        return {
            totalProducts,
            lowStockProducts,
            totalValueByCost,
            totalValueByPrice
        };
    };

    const stats = calculateStats();

    return (
        <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
            {/* Main Content Area */}
            <div className="flex-1 flex flex-col min-h-0">
                {/* Stats Cards */}
                <div className="px-4 sm:px-6 py-3 bg-white border-b border-gray-200">
                    <div className={`grid grid-cols-1 gap-4 ${isWarehouse() ? 'md:grid-cols-2' : 'lg:grid-cols-4 md:grid-cols-2'}`}>
                        <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                                <Package className="h-6 w-6 text-blue-600" />
                            </div>
                            <div>
                                <dt className="text-xs font-medium text-gray-500 uppercase">Toplam Ürün</dt>
                                <dd className="text-lg font-bold text-gray-900">{stats.totalProducts}</dd>
                            </div>
                        </div>

                        <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                                <AlertTriangle className="h-6 w-6 text-red-600" />
                            </div>
                            <div>
                                <dt className="text-xs font-medium text-gray-500 uppercase">Stok Dışı</dt>
                                <dd className="text-lg font-bold text-gray-900">{stats.lowStockProducts}</dd>
                            </div>
                        </div>

                        {!isWarehouse() && (
                            <>
                                <div className="flex items-center space-x-3">
                                    <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-blue-100 text-blue-600">
                                        <span className="font-bold text-sm">₺</span>
                                    </div>
                                    <div>
                                        <dt className="text-xs font-medium text-gray-500 uppercase">Alış Değeri</dt>
                                        <dd className="text-lg font-bold text-gray-900">{stats.totalValueByCost.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺</dd>
                                    </div>
                                </div>

                                <div className="flex items-center space-x-3">
                                    <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-green-100 text-green-600">
                                        <span className="font-bold text-sm">₺</span>
                                    </div>
                                    <div>
                                        <dt className="text-xs font-medium text-gray-500 uppercase">Satış Değeri</dt>
                                        <dd className="text-lg font-bold text-gray-900">{stats.totalValueByPrice.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺</dd>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <PageToolbar
                    right={
                        <>
                            <button
                                onClick={() => fetchProducts()}
                                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                title="Yenile"
                            >
                                <RefreshCw className="w-4 h-4" />
                                Yenile
                            </button>
                            <label className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={showLowStock}
                                    onChange={(e) => setShowLowStock(e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                Stok Dışı
                            </label>
                        </>
                    }
                >
                    <div className="relative w-full sm:w-80">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Ürün ara (isim, ürün kodu...)"
                            className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-700 placeholder-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {canCreateProduct() && (
                        <button
                            onClick={() => setShowAddModal(true)}
                            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700"
                        >
                            <Plus className="w-4 h-4" />
                            Yeni Ürün
                        </button>
                    )}
                </PageToolbar>


                {/* Products Table */}
                <div className="flex-1 bg-white laptop-optimized-table overflow-y-auto">
                    {loading ? (
                        <div className="p-12 text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-4 text-gray-600">Ürünler yükleniyor...</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                    <tr className="border-b border-gray-200">
                                        <th
                                            className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                                            onClick={() => handleSort('name')}
                                        >
                                            <div className="flex items-center">
                                                Ürün Adı
                                                {sortField === 'name' && (
                                                    <span className="ml-1">
                                                        {sortDirection === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                                            onClick={() => handleSort('product_code')}
                                        >
                                            <div className="flex items-center">
                                                Ürün Kodu
                                                {sortField === 'product_code' && (
                                                    <span className="ml-1">
                                                        {sortDirection === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                        <th
                                            className="px-3 py-2 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                                            onClick={() => handleSort('brand')}
                                        >
                                            <div className="flex items-center">
                                                Marka
                                                {sortField === 'brand' && (
                                                    <span className="ml-1">
                                                        {sortDirection === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                        {!isWarehouse() && (
                                            <>
                                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                                    Alış Fiyatı
                                                </th>
                                                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                                    Satış Fiyatı
                                                </th>
                                            </>
                                        )}
                                        <th
                                            className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                                            onClick={() => handleSort('stock')}
                                        >
                                            <div className="flex items-center justify-center">
                                                Stok
                                                {sortField === 'stock' && (
                                                    <span className="ml-1">
                                                        {sortDirection === 'asc' ? '↑' : '↓'}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                        <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider w-24">
                                            İşlemler
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {products.map((product) => (
                                        <tr
                                            key={product.id}
                                            className="hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100 laptop-table-row"
                                            onDoubleClick={() => navigate(`/products/${product.id}`)}
                                        >
                                            {/* Ürün Adı */}
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {product.name}
                                                </div>
                                            </td>

                                            {/* Ürün Kodu */}
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">
                                                    {product.product_code || '-'}
                                                </div>
                                            </td>

                                            {/* Marka */}
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <div className="text-sm text-gray-900">
                                                    {product.brand || '-'}
                                                </div>
                                            </td>

                                            {!isWarehouse() && (
                                                <>
                                                    {/* Alış Fiyatı */}
                                                    <td className="px-3 py-2 whitespace-nowrap text-right">
                                                        <div className="text-sm font-semibold text-gray-900">
                                                            {(parseFloat(product.cost) || 0).toFixed(2)} ₺
                                                        </div>
                                                    </td>

                                                    {/* Satış Fiyatı */}
                                                    <td className="px-3 py-2 whitespace-nowrap text-right">
                                                        <div className="text-sm font-semibold text-gray-900">
                                                            {(parseFloat(product.price) || 0).toFixed(2)} ₺
                                                        </div>
                                                        {product.cost && product.price && parseFloat(product.cost) > 0 && (
                                                            <div className="text-xs text-green-600">
                                                                +{((parseFloat(product.price) - parseFloat(product.cost)) / parseFloat(product.cost) * 100).toFixed(1)}%
                                                            </div>
                                                        )}
                                                    </td>
                                                </>
                                            )}

                                            {/* Stok */}
                                            <td className="px-3 py-2 whitespace-nowrap text-center">
                                                <div className={`text-sm font-bold ${product.stock > 0 ? 'text-green-600' : 'text-red-600'
                                                    }`}>
                                                    {product.stock || 0}
                                                </div>
                                                {product.stock <= 0 && (
                                                    <div className="text-xs text-red-500">Stok Dışı</div>
                                                )}
                                            </td>
                                            {/* İşlemler */}
                                            <td className="px-3 py-2 whitespace-nowrap text-center">
                                                <div className="flex items-center justify-center space-x-1">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/products/${product.id}`);
                                                        }}
                                                        className="inline-flex items-center p-1 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
                                                        title="Detay"
                                                    >
                                                        <Eye className="w-3 h-3" />
                                                    </button>
                                                    {canUpdateProduct() && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEdit(product);
                                                            }}
                                                            className="inline-flex items-center p-1 border border-transparent text-xs font-medium rounded text-amber-700 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-1 focus:ring-amber-500 transition-colors"
                                                            title="Düzenle"
                                                        >
                                                            <Edit className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                    {canDeleteProduct() && (
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDelete(product.id);
                                                            }}
                                                            className="inline-flex items-center p-1 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-1 focus:ring-red-500 transition-colors"
                                                            title="Sil"
                                                        >
                                                            <Trash2 className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="bg-white px-4 sm:px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className="relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Önceki
                            </button>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Sonraki
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-xs text-gray-600">
                                    Sayfa <span className="font-medium">{currentPage}</span> / <span className="font-medium">{totalPages}</span>
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded shadow-sm -space-x-px">
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                        disabled={currentPage === 1}
                                        className="relative inline-flex items-center px-2 py-1.5 rounded-l border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Önceki
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                        disabled={currentPage === totalPages}
                                        className="relative inline-flex items-center px-2 py-1.5 rounded-r border border-gray-300 bg-white text-xs font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Sonraki
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Add/Edit Modal */}
            {(showAddModal || showEditModal) && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] overflow-hidden">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <Package className="h-6 w-6 text-white" />
                                    <h2 className="text-xl font-semibold text-white">
                                        {showEditModal ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}
                                    </h2>
                                </div>
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="text-blue-100 hover:text-white transition-colors"
                                >
                                    ×
                                </button>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div className="max-h-[calc(95vh-120px)] overflow-y-auto">
                            <form onSubmit={handleSubmit} className="p-6">
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    {/* Sol Kolon - Ürün Bilgileri */}
                                    <div className="space-y-6">
                                        <div className="bg-gray-50 rounded-lg p-6">
                                            <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                                                <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                                                Ürün Bilgileri
                                            </h3>
                                            <div className="space-y-6">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Ürün Adı <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleInputChange}
                                                        required
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Ürün Kodu
                                                    </label>
                                                    <input
                                                        type="text"
                                                        name="product_code"
                                                        value={formData.product_code}
                                                        onChange={handleInputChange}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    />
                                                </div>


                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Marka</label>
                                                    <input
                                                        type="text"
                                                        name="brand"
                                                        value={formData.brand}
                                                        onChange={handleInputChange}
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        list="brands"
                                                    />
                                                    <datalist id="brands">
                                                        {filters.brands && filters.brands.map(brand => (
                                                            <option key={brand} value={brand} />
                                                        ))}
                                                    </datalist>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Sağ Kolon - Fiyat ve Stok */}
                                    <div className="space-y-6">
                                        <div className="bg-green-50 rounded-lg p-6">
                                            <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
                                                <div className="w-2 h-2 bg-green-600 rounded-full mr-3"></div>
                                                {isWarehouse() ? 'Stok Bilgileri' : 'Fiyat ve Stok Bilgileri'}
                                            </h3>
                                            <div className="space-y-6">
                                                {!isWarehouse() && (
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">Alış Fiyatı (₺)</label>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                name="cost"
                                                                value={formData.cost}
                                                                onChange={handleInputChange}
                                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            />
                                                        </div>

                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-2">Satış Fiyatı (₺)</label>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                name="price"
                                                                value={formData.price}
                                                                onChange={handleInputChange}
                                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            />
                                                        </div>
                                                    </div>
                                                )}

                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">Mevcut Stok</label>
                                                    <input
                                                        type="number"
                                                        name="stock"
                                                        value={formData.stock}
                                                        onChange={handleInputChange}
                                                        min="0"
                                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                    />
                                                </div>

                                                {/* Stok Ekleme - Sadece düzenleme modunda göster */}
                                                {showEditModal && (
                                                    <div className="bg-blue-100 rounded-lg p-4">
                                                        <h4 className="text-sm font-medium text-gray-700 mb-3">Stok Ekle</h4>
                                                        <div className="flex gap-2">
                                                            <input
                                                                type="number"
                                                                value={stockToAdd}
                                                                onChange={(e) => setStockToAdd(e.target.value)}
                                                                placeholder="Eklenecek miktar"
                                                                min="0"
                                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={handleAddStock}
                                                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                                                            >
                                                                Ekle
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Buttons */}
                                <div className="flex justify-end gap-4 pt-8 mt-8 border-t border-gray-200">
                                    <button
                                        type="button"
                                        onClick={resetForm}
                                        className="px-8 py-3 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium transition-colors"
                                    >
                                        İptal
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
                                    >
                                        {showEditModal ? 'Güncelle' : 'Kaydet'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            


        </div>
    );
};

export default Products;
